import { Datetime } from '@aigc/components';
import { useOverlay } from '@aigc/overlay-hook';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { Notification } from '@novu/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Space,
  Table,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import DOMPurify from 'dompurify';
import { useCallback, useMemo } from 'react';
import {
  archiveAnnouncement,
  deleteAnnouncement,
  queryAnnouncements,
} from '../api';
import { AnnouncementCreateModal } from './announcement-create-modal';
import { AnnouncementDetailModal } from './announcement-detail-modal';

export function AnnouncementTable() {
  const { modal, message } = App.useApp();
  const [openOverlay] = useOverlay();
  const { data, isFetching, refetch } = useQuery(queryAnnouncements);
  const { mutateAsync: doDelete } = useMutation({
    mutationKey: queryAnnouncements.queryKey,
    mutationFn: deleteAnnouncement,
  });
  const { mutateAsync: doArchive } = useMutation({
    mutationKey: queryAnnouncements.queryKey,
    mutationFn: archiveAnnouncement,
  });

  const openCreate = useCallback(() => {
    const ref = openOverlay(<AnnouncementCreateModal />);
    ref.afterClosed.then(() => refetch());
  }, [openOverlay, refetch]);

  const handleDelete = useCallback(
    (row: Notification) => {
      modal.confirm({
        title: '确认删除',
        content: `确定要删除公告 "${row.subject}" 吗？删除后将立即从在线用户页面移除。`,
        okText: '删除',
        okButtonProps: { variant: 'filled', color: 'danger' },
        cancelText: '取消',
        onOk: async () => {
          try {
            await doDelete(row.id);
            message.success('删除成功');
            refetch();
          } catch {
            message.error('删除失败');
          }
        },
      });
    },
    [doDelete, message, modal, refetch],
  );

  const handleArchive = useCallback(
    (row: Notification) => {
      modal.confirm({
        title: '确认归档',
        content: `确定要归档公告 "${row.subject}" 吗？归档后将不再显示在在线用户页面，但仍可在后台查看。`,
        okText: '归档',
        okButtonProps: { variant: 'filled', color: 'danger' },
        cancelText: '取消',
        onOk: async () => {
          try {
            await doArchive(row.id);
            message.success('归档成功');
            refetch();
          } catch {
            message.error('归档失败');
          }
        },
      });
    },
    [doArchive, message, modal, refetch],
  );

  const columns = useMemo<TableColumnsType<Notification>>(
    () => [
      {
        title: '标题',
        width: 180,
        render: (_, row) => row.subject,
      },
      {
        title: '内容',
        ellipsis: true,
        render: (_, row) => <PlainText html={row.body}></PlainText>,
      },
      {
        title: '创建时间',
        width: 180,
        render: (_, row) => <Datetime value={row.createdAt} />,
      },
      {
        title: '操作',
        fixed: 'right',
        width: 200,
        render: (_, row) => (
          <Space>
            <Button
              variant="filled"
              color="primary"
              size="small"
              onClick={() => {
                openOverlay(<AnnouncementDetailModal data={row} />);
              }}
            >
              查看
            </Button>
            {!row.isArchived && (
              <Tooltip title="归档后将不再显示在在线用户页面，但仍可在后台查看">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleArchive(row)}
                >
                  归档
                </Button>
              </Tooltip>
            )}
            <Button
              variant="filled"
              color="danger"
              size="small"
              onClick={() => handleDelete(row)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [handleArchive, handleDelete, openOverlay],
  );

  return (
    <div className="h-full overflow-auto bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>
          公告管理
        </Typography.Title>
        <div className="flex-auto" />
        <Space>
          <Button
            variant="filled"
            color="default"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
          />
          <Button
            variant="solid"
            color="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            新增公告
          </Button>
        </Space>
      </div>
      <Table
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={data ?? []}
        loading={isFetching}
        pagination={false}
        scroll={{ x: 900 }}
      />
    </div>
  );
}

const PlainText = (props: { html: string }) => {
  return useMemo(() => {
    const fragment = document.createDocumentFragment();
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(props.html);
    fragment.appendChild(div);
    return fragment.textContent;
  }, [props.html]);
};
